import { useState } from 'react';
import {
  deleteContract,
  dismissContractReminder,
  markContractReviewed,
  publishContractToDrive,
  regenerateKeyRisks,
  terminateContract,
  updateContract,
} from './contractDetailApi';
import {
  serializeClauseField,
  toDateInput,
} from './contractDetailUtils';

export default function useContractDetailActions({
  contract,
  contractId,
  navigate,
  refreshContract,
  setContract,
  showToast,
}) {
  const [openClause, setOpenClause] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteDriveFolder, setDeleteDriveFolder] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showTerminateConfirm, setShowTerminateConfirm] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showDetailedSummaryModal, setShowDetailedSummaryModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newExecutedDate, setNewExecutedDate] = useState('');
  const [terminationReason, setTerminationReason] = useState('');
  const [dsEditing, setDsEditing] = useState(false);
  const [dsDraft, setDsDraft] = useState('');
  const [dsSaving, setDsSaving] = useState(false);
  const [dsError, setDsError] = useState('');
  const [editingClauseNum, setEditingClauseNum] = useState(null);
  const [clauseDraft, setClauseDraft] = useState({});
  const [clauseSaving, setClauseSaving] = useState(false);
  const [clauseError, setClauseError] = useState('');
  const [regeneratingKeyRisks, setRegeneratingKeyRisks] = useState(false);

  const openStatusModal = () => {
    setNewStatus(contract?.status || '');
    setNewExecutedDate(contract?.executedDate || '');
    setShowStatusModal(true);
  };

  const handleStatusChange = async () => {
    try {
      const payload = { status: newStatus };
      if (newExecutedDate && newExecutedDate !== (contract?.executedDate || '')) {
        payload.executedDate = newExecutedDate;
      }
      await updateContract(contractId, payload);
      setShowStatusModal(false);
      showToast('Status updated', 'success');
      refreshContract();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const dismissReminder = async (kind, reenable = false) => {
    try {
      await dismissContractReminder(contractId, kind, reenable);
      showToast(
        reenable
          ? `${kind === 'renewal' ? 'Renewal' : 'Notice'} reminder re-enabled`
          : `${kind === 'renewal' ? 'Renewal' : 'Notice'} reminder dismissed`,
        'success'
      );
      refreshContract();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleMarkReviewed = async () => {
    try {
      await markContractReviewed(contractId);
      showToast('Marked as reviewed', 'success');
      refreshContract();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleTerminate = async () => {
    try {
      await terminateContract(contractId, terminationReason);
      setShowTerminateConfirm(false);
      setTerminationReason('');
      showToast('Contract terminated', 'success');
      refreshContract();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleRegenerateKeyRisks = async () => {
    setRegeneratingKeyRisks(true);
    try {
      const updated = await regenerateKeyRisks(contractId);
      setContract((prev) => (prev ? { ...prev, keyRisks: updated.keyRisks, keyCommitments: updated.keyCommitments, financialTerms: updated.financialTerms } : prev));
      showToast('Key terms regenerated', 'success');
    } catch (err) {
      showToast(err.message || 'Could not regenerate key terms', 'error');
    } finally {
      setRegeneratingKeyRisks(false);
    }
  };

  const handleClearKeyRisks = async () => {
    try {
      const updated = await updateContract(contractId, { keyRisks: [], keyCommitments: [], financialTerms: [] });
      setContract((prev) => (prev ? { ...prev, keyRisks: updated.keyRisks || [], keyCommitments: updated.keyCommitments || [], financialTerms: updated.financialTerms || [] } : prev));
      showToast('Key terms cleared', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Inline edit/delete/add of a single key-terms list (keyRisks / keyCommitments
  // / financialTerms / openIssues) from the detail card. Partial PUT — only the
  // one field is written.
  const handleSaveKeyTerms = async (field, list) => {
    try {
      const updated = await updateContract(contractId, { [field]: list });
      setContract((prev) => (prev ? { ...prev, [field]: updated[field] ?? list } : prev));
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handlePublishToDrive = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const res = await publishContractToDrive(contractId) || {};
      const n = res.published || 0;
      showToast(
        n > 0
          ? `Saved ${n} file${n === 1 ? '' : 's'} to Drive`
          : 'Drive is already up to date',
        'success'
      );
      refreshContract();
    } catch (err) {
      showToast(err.message || 'Save to Drive failed', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const data = await deleteContract(contractId, { deleteDriveFolder }) || {};
      if (data.driveWarning) showToast(data.driveWarning, 'warning');
      else if (data.driveFolderTrashed && data.counterpartyFolderTrashed) {
        showToast('Contract deleted; Drive folder + empty counterparty folder moved to Trash', 'success');
      } else if (data.driveFolderTrashed) {
        showToast('Contract deleted; Drive folder moved to Trash', 'success');
      } else {
        showToast('Contract deleted', 'success');
      }
      navigate('/contracts');
    } catch (err) {
      showToast(err.message, 'error');
      setDeleting(false);
    }
  };

  const beginClauseEdit = (clause) => {
    const draft = {};
    clause.fields.forEach((field) => {
      const raw = field.value;
      if (field.type === 'date') draft[field.key] = toDateInput(raw);
      else if (raw == null) draft[field.key] = '';
      else draft[field.key] = String(raw);
    });
    setClauseDraft(draft);
    setEditingClauseNum(clause.num);
    setClauseError('');
    setOpenClause(clause.num);
  };

  const cancelClauseEdit = () => {
    setEditingClauseNum(null);
    setClauseDraft({});
    setClauseError('');
  };

  const saveClauseEdit = async (clause) => {
    setClauseSaving(true);
    setClauseError('');
    try {
      const patch = {};
      clause.fields.forEach((field) => {
        const originalForInput = field.type === 'date'
          ? toDateInput(field.value)
          : (field.value == null ? '' : String(field.value));
        const nextStr = clauseDraft[field.key] ?? '';
        if (nextStr !== originalForInput) {
          patch[field.key] = serializeClauseField(field, nextStr);
        }
      });
      if (Object.keys(patch).length === 0) {
        cancelClauseEdit();
        showToast('No changes to save', 'info');
        return;
      }
      const updated = await updateContract(contractId, patch);
      setContract((prev) => ({ ...prev, ...updated }));
      showToast(`${clause.label} updated`, 'success');
      cancelClauseEdit();
    } catch (err) {
      setClauseError(err.message);
    } finally {
      setClauseSaving(false);
    }
  };

  const handleDetailedSummarySave = async () => {
    setDsSaving(true);
    setDsError('');
    try {
      const updated = await updateContract(contractId, { detailedSummary: dsDraft });
      setContract((prev) => ({ ...prev, ...updated }));
      setDsEditing(false);
      showToast('Summary updated', 'success');
    } catch (err) {
      setDsError(err.message);
    } finally {
      setDsSaving(false);
    }
  };

  return {
    beginClauseEdit,
    cancelClauseEdit,
    clauseDraft,
    clauseError,
    clauseSaving,
    deleteDriveFolder,
    deleting,
    dismissReminder,
    dsDraft,
    dsEditing,
    dsError,
    dsSaving,
    editingClauseNum,
    handleDelete,
    handleDetailedSummarySave,
    handleMarkReviewed,
    handlePublishToDrive,
    handleStatusChange,
    handleTerminate,
    handleRegenerateKeyRisks,
    handleClearKeyRisks,
    handleSaveKeyTerms,
    regeneratingKeyRisks,
    publishing,
    newExecutedDate,
    newStatus,
    openClause,
    openStatusModal,
    saveClauseEdit,
    setClauseDraft,
    setDeleteDriveFolder,
    setDsDraft,
    setDsEditing,
    setNewExecutedDate,
    setNewStatus,
    setOpenClause,
    setShowDeleteConfirm,
    setShowDetailedSummaryModal,
    setShowScanModal,
    setShowStatusModal,
    setShowTerminateConfirm,
    setTerminationReason,
    showDeleteConfirm,
    showDetailedSummaryModal,
    showScanModal,
    showStatusModal,
    showTerminateConfirm,
    terminationReason,
  };
}
